import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { MessageSquareQuote, PenLine, Check } from 'lucide-react';
import { MoodPicker } from './MoodPicker';

/**
 * The blocks that sit UNDER the assessment snapshot on the side rail: where they are today,
 * feedback waiting on them, whether they have written yet, and the day's line.
 * The rail loads on every dashboard page, so this reads one cheap endpoint.
 */
export const CompanionRailBlocks = ({ onOpenFull }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/companion/rail`)
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const go = (path) => { onOpenFull?.(); navigate(path); };

  if (!data) return null;
  const wrote = data.journal?.wrote_today;

  return (
    <div className="space-y-5" data-testid="rail-blocks">
      <section data-testid="rail-mood">
        <h3 className="text-xs uppercase tracking-wider text-white/40 mb-2">Today</h3>
        <MoodPicker
          moods={data.moods}
          latest={data.mood?.logged_today ? data.mood.latest : null}
          loggedToday={data.mood?.logged_today}
          onLogged={load}
          compact
        />
      </section>

      {data.feedback_unread > 0 && (
        <button
          onClick={() => go('/dashboard/companion/work')}
          className="w-full text-left rounded-xl border border-amber-300/40 bg-amber-300/[0.07]
                     px-4 py-3 hover:bg-amber-300/[0.12] transition-colors duration-200"
          data-testid="rail-feedback-waiting"
        >
          <span className="text-sm text-amber-100">
            {data.feedback_unread === 1
              ? 'A practitioner has written back'
              : `${data.feedback_unread} pieces of feedback waiting`}
          </span>
          <span className="block text-[11px] text-amber-200/60 mt-0.5">Open your work →</span>
        </button>
      )}

      <button
        onClick={() => go('/dashboard/companion/journal')}
        className="w-full text-left rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3
                   hover:bg-white/[0.07] transition-colors duration-200"
        data-testid="rail-journal"
      >
        <span className="flex items-center gap-2 text-sm text-white/85">
          {wrote ? <Check className="w-3.5 h-3.5 text-teal-300" />
                 : <PenLine className="w-3.5 h-3.5 text-white/50" />}
          {wrote ? 'You wrote today' : 'Nothing written today'}
        </span>
        <span className="block text-[11px] text-white/40 mt-0.5">
          {wrote ? 'Add another entry →' : 'Open your journal →'}
        </span>
      </button>

      {data.quote && (
        <section data-testid="rail-quote">
          <h3 className="text-xs uppercase tracking-wider text-white/40 mb-2">
            Today&apos;s line
          </h3>
          <figure className="rounded-xl border border-violet-300/25 bg-violet-400/[0.06] p-4">
            <MessageSquareQuote className="w-4 h-4 text-violet-300/70 mb-2" />
            <blockquote className="text-sm text-white/85 leading-relaxed">
              {data.quote.text}
            </blockquote>
            <figcaption className="text-[11px] text-white/40 mt-2">{data.quote.source}</figcaption>
          </figure>
        </section>
      )}
    </div>
  );
};

export default CompanionRailBlocks;
